import React, { useEffect, useState } from 'react';
import { CopyOutlined, LeftOutlined, QuestionCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { Button, Card, Form, Input, Switch, Row, Col, notification, Tag, message, Tooltip } from 'antd';
import { useTranslation } from 'react-i18next';
import { useForm } from 'antd/lib/form/Form';

import './TingeeThirdParty.scss';
import ThirdPartyIntegration from 'services/thirdPartyIntegrationService';
import BasicButtonPrimary from 'components/BasicButtonPrimary';
import { THIRDPARTY_CODE } from 'constants/app';
import { getThirdPartyIdFromStationIntegrationList } from './helpers/thirdPartyIntegrationHelper';



const ZalopayBusinessThirdParty = ({ id, setOpenContent, setId }) => {
  const { t: translation } = useTranslation();
  const [form] = useForm();
  const [loading, setLoading] = useState(false);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    id && handleGetData(id);
  }, [id]);

  const handleGetData = async (targetId = id) => {
    if (!targetId) return;
    try {
      setLoading(true)
      const res = await ThirdPartyIntegration.getThirdPartyById({ id: targetId });
      handleSetupData(res)
      setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  };

  const handleSetupData = (res) => {
    const partyRequiredData = JSON.parse(res?.partyRequiredData || "{}");
    const integrationMetadata = JSON.parse(res?.integrationMetadata || "{}");
    form.setFieldsValue({
      partyUrl: partyRequiredData?.partyUrl,
      app_id: partyRequiredData?.app_id,
      secretKeyTingee: partyRequiredData.secretKeyTingee,
      mac_key: partyRequiredData.mac_key,
      callback_key: partyRequiredData.callback_key,
      callback_url: partyRequiredData?.callback_url,
      storeId: integrationMetadata?.storeId,
      posId: integrationMetadata?.posId,
      isConnected: res.partyActiveStatus,
      isEnable: res?.partyEnableStatus,
    });
    setIsConnected(res.partyActiveStatus === 1);
  }

  const handleTestZaloPay = async (showError = true) => {
    try {
      const values = await form.getFieldsValue();
      const payload = {
        partyUrl: values.partyUrl,
        app_id: values.app_id,
        mac_key: values.mac_key,
        callback_key: values.callback_key,
        callback_url: values.callback_url,
        amount: 10000,
      };
      const result = await ThirdPartyIntegration.testConfigsZaloPayBusiness(payload);
      if (result) {
        setIsConnected(true);
        form.setFieldsValue({ isConnected: true });
        notification['success']({
          message: "",
          description: 'Test ZaloPay successful, connection activated'
        });
        return true;
      }
      return false;
    } catch (error) {
      showError && notification['error']({
        message: "",
        description: translation('zalopay_business.testError') || error?.message || 'Test ZaloPay failed'
      })
      return false;
    }
  };

  const handleSubmit = async () => {
    await form.validateFields();
    handleUpdateConfig();
  };

  const handleUpdateConfig = async () => {
    try {
      let values = await form.getFieldsValue();
      let isConnectedLocal = isConnected;
      if (!isConnectedLocal) {
        const testSuccess = await handleTestZaloPay()
        if(!testSuccess) return
        isConnectedLocal = true;
        setIsConnected(true);
        values = await form.getFieldsValue();
      }
      const payload = {
        data: {
          partyRequiredData: {
            partyUrl: values.partyUrl,
            app_id: values.app_id,
            mac_key: values.mac_key,
            callback_key: values.callback_key,
            callback_url: values.callback_url
          },
          integrationMetadata: {
            storeId: values.storeId,
            posId: values.posId,
            enableQrDisplay: values.isEnable ? 1 : 0,
          },
          partyActiveStatus: isConnectedLocal ? 1 : 0,
          partyEnableStatus: values.isEnable ? 1 : 0,
        }
      };
      await ThirdPartyIntegration.updateConfigsZaloPayBusiness(payload);
      const updatedThirdPartyId = await getThirdPartyIdFromStationIntegrationList(THIRDPARTY_CODE.ZALOPAY_BUSINESS);
      if (!updatedThirdPartyId) {
        throw new Error('ZALOPAY_ID_NOT_FOUND_FROM_LIST');
      }
      if (Number(updatedThirdPartyId) !== Number(id)) {
        setId(updatedThirdPartyId);
      } else {
        await handleGetData(updatedThirdPartyId);
      }
      notification["success"]({
        message: "",
        description: translation("tingee.updateSuccess"),
      });
    } catch (error) {
      notification['error']({
        message: "",
        description: translation('tingee.updateError')
      })
    }
  };

  return (
    <div className='tingee-noti'>
      <Button type="default" icon={<LeftOutlined />} onClick={() => { setOpenContent("") }}>
      </Button>
      <Card loading={loading} className='telegram-noti_content'>
        <div className='mt-2'>
          <Form load form={form} onSubmit={handleSubmit} layout="vertical">
            <Row gutter={[48, 16]}>
              <Col
                xs={{ order: 2, span: 24 }}
                md={{ order: 1, span: 10 }}
                span={10}
              >
                <div className="section-title mb-2 mt-3">{translation('zalopay_business.accountSetup')}</div>
                <Form.Item
                  required
                  label={translation("zalopay_business.partyUrl")}
                  name="partyUrl"
                  rules={[
                    {
                      required: true,
                      message: translation('zalopay_business.validateRequired'),
                    },
                  ]}
                >
                  <Input type="text" placeholder={translation("zalopay_business.partyUrl")} />
                </Form.Item>
                <Form.Item
                  required
                  label={translation("zalopay_business.app_id")}
                  name="app_id"
                  rules={[
                    {
                      required: true,
                      message: translation('zalopay_business.validateRequired'),
                    },
                  ]}
                >
                  <Input type="text" placeholder={translation("zalopay_business.app_id")} />
                </Form.Item>
                <Form.Item
                  required
                  label={translation("zalopay_business.mac_key")}
                  name="mac_key"
                  rules={[
                    {
                      required: true,
                      message: translation('zalopay_business.validateRequired'),
                    },
                  ]}
                >
                  <Input type="text" placeholder={translation("zalopay_business.mac_key")} />
                </Form.Item>
                <Form.Item
                  required
                  label={translation("zalopay_business.callback_key")}
                  name="callback_key"
                  rules={[
                    {
                      required: true,
                      message: translation('zalopay_business.validateRequired'),
                    },
                  ]}
                >
                  <Input type="text" placeholder={translation("zalopay_business.callback_key")} />
                </Form.Item>
                <Form.Item
                  required
                  label={translation("zalopay_business.callback_url")}
                  name="callback_url"
                >
                  <Input
                    readOnly
                    placeholder={translation("zalopay_business.callback_url")}
                    suffix={
                      <CopyOutlined
                        style={{ cursor: "pointer", color: "#1890ff" }}
                        onClick={() => {
                          const value = form.getFieldValue("callback_url");
                          navigator.clipboard.writeText(value).then(() => {
                            message.success(translation("copied_successfully"));
                          });
                        }}
                      />
                    }
                  />
                </Form.Item>
                <Form.Item label={translation('telegram.active')} name="isEnable" valuePropName="checked">
                  <Switch />
                </Form.Item>
                <Form.Item className='pt-1' label={translation('telegram.connectionStatus')}>
                  <Tag color={form.getFieldValue("isConnected") ? 'green' : ''}>
                    {form.getFieldValue("isConnected") ? translation('telegram.connected') : translation('telegram.notConnected')}
                  </Tag>
                </Form.Item>
                <BasicButtonPrimary
                  style={{ marginRight: 10 }} onclick={() => handleSubmit("submit")} className='mr-2' htmlType="submit"
                  text={translation('telegram.updateButton')}
                />
              </Col>
              <Col
                xs={{ order: 2, span: 24 }}
                md={{ order: 1, span: 10 }}
                span={10}
              >
                <div className="section-title mb-2 mt-3 device-setup-title">
                  {translation('zalopay_business.deviceSetup')}
                  <Tooltip title={translation("zalopay_business.deviceSetupHint")}>
                    <InfoCircleOutlined className="device-setup-icon" />
                  </Tooltip>
                </div>
                <Form.Item
                  label={translation("zalopay_business.storeId")}
                  name="storeId"
                >
                  <Input type="text" placeholder={translation("zalopay_business.storeId")} />
                </Form.Item>
                <Form.Item
                  label={translation("zalopay_business.posId")}
                  name="posId"
                >
                  <Input type="text" placeholder={translation("zalopay_business.posId")} />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      </Card>
    </div>
  );
};

export default ZalopayBusinessThirdParty;
